import { CharacterVisual } from '../engine/CharacterVisual';
import * as Phaser from 'phaser';
import { PhaserSceneAdapter } from '../engine/PhaserSceneAdapter';
import { PhaserEntityFactoryAdapter } from '../engine/PhaserEntityFactoryAdapter';
import PoolManager from '../managers/PoolManager';
import { WeaponSystem } from '../systems/WeaponSystem';
import { WaveSpawnerSystem } from '../systems/WaveSpawnerSystem';
import { PlayerController } from '../systems/PlayerController';
import { CollisionSystem } from '../systems/CollisionSystem';
import { ExperienceSystem } from '../systems/ExperienceSystem';
import { LootSystem } from '../systems/LootSystem';
import { BossEncounterSystem } from '../systems/BossEncounterSystem';
import { BiomeBackgroundRenderer, MapSystem } from '../systems/MapSystem';
import { EventBus } from '../core/EventBus';
import { LevelUpScreen } from '../ui/LevelUpScreen';
import { PlayerStats } from '../components/PlayerStats';
import { HUD } from '../ui/HUD';
import { EnemyFactory } from '../factories/EnemyFactory';
import { AudioManager } from '../managers/AudioManager';
import { UpgradeSystem, UpgradeOption } from '../systems/UpgradeSystem';
import { LocalizationManager } from '../managers/LocalizationManager';
import { FloatingText } from '../ui/FloatingText';
import { ParticleManager } from '../managers/ParticleManager';
import { ChestOpenScreen } from '../ui/ChestOpenScreen';
import { PauseScreen } from '../ui/PauseScreen';
import { ContentRegistry, loadContentRegistry, WeaponBehaviorType } from '../data';
import { createWeaponRuntimeConfig } from '../systems/WeaponTypes';
import { createLogger } from '../utils/Logger';
import { ensureFallbackTextures, getVisualDiagnosticsSummary, TextureFallbackSummary } from '../utils/TextureFallbacks';
import { createVisualTextureResolver, VisualTextureResolver } from '../utils/VisualTextureResolver';
import { getAppVersionSnapshot } from '../config/AppVersion';
import {
  applyRuntimeTestingOverrides,
  DEFAULT_RUNTIME_TESTING_MODE_STATE,
  parseRuntimeTestingModeState,
  RuntimeTestingModeState,
  updateRuntimeTestingModeState
} from '../config/RuntimeTestingMode';
import { DiagnosticsOverlay } from '../ui/DiagnosticsOverlay';
import { MobileInputController, MobileInputSnapshot } from '../systems/MobileInputController';
import { MobileControlsOverlay } from '../ui/MobileControlsOverlay';
import { OrientationOverlay, OrientationSnapshot } from '../ui/OrientationOverlay';
import { BossEncounterEventMetadata, isBossLookingPayload } from '../systems/BossEventPayloads';
import { CombatFeedbackSystem } from '../systems/CombatFeedbackSystem';
import { getSharedProfileRuntime, ProfileRuntime } from '../managers/ProfileRuntime';
import { PlayerProfile, RunResult, RunSummary } from '../data/ProfileState';
import {
  createDebugCommandFailure,
  createDebugCommandSuccess,
  DebugCommandResult,
  DebugCommandSource,
  DebugStatsSnapshot,
  DebugStatsSnapshotPayload,
  ForceWaveDebugPayload,
  GiveWeaponDebugPayload,
  resolveDebugCommandSource,
  SkipWaveDebugPayload,
  SpawnEnemyDebugPayload,
  SpawnEnemyDebugResult,
  WaveDebugResult
} from '../systems/DebugCommandTypes';

const CAMERA_FOLLOW_LERP_X = 0.1;
const CAMERA_FOLLOW_LERP_Y = 0.1;
const CAMERA_INITIAL_ZOOM = 1;

export interface WeaponDamageSmokeResult {
  weaponId: string;
  appeared: boolean;
  damaged: boolean;
  startHealth: number;
  endHealth: number;
  poolType?: string;
  error?: string;
}

export interface ActiveBossEncounterE2ESnapshot {
  encounterId: string;
  enemyId: string;
  displayName: string;
  currentHealth: number;
  maxHealth: number;
  requiredForVictory: boolean;
}

export type RewardSmokeResult =
  | { ok: false; reason: string }
  | {
      ok: true;
      mode: 'ordinary' | 'evolution';
      particleDeltas: { opening: number; reward: number; repeatedClose: number };
      inventoryBefore: string[];
      inventoryAfter: string[];
      collectButton: { x: number; y: number };
      visibleAfterClose: boolean;
    };

export interface CombatFeedbackSmokeResult {
  effectKey: string;
  particleKey?: string;
  cameraSuppressed: boolean;
  audioKey?: string;
}

export class MainScene extends Phaser.Scene {
  private poolManager!: PoolManager;
  private weaponSystem!: WeaponSystem;
  private waveSpawnerSystem!: WaveSpawnerSystem;
  private enemyFactory!: EnemyFactory;
  private entityFactoryAdapter!: PhaserEntityFactoryAdapter;
  private playerController!: PlayerController;
  private experienceSystem!: ExperienceSystem;
  private lootSystem!: LootSystem;
  private bossEncounterSystem!: BossEncounterSystem;
  private collisionSystem!: CollisionSystem;
  private mapSystem!: MapSystem;
  private eventBus!: EventBus;
  private player!: Phaser.Physics.Arcade.Sprite;
  private enemyGroup!: Phaser.Physics.Arcade.Group;
  private levelUpScreen!: LevelUpScreen;
  private isPaused: boolean = false;
  private bossesKilled: number = 0;
  private readonly killedRequiredBossEncounterIds = new Set<string>();
  private playerStats!: PlayerStats;
  private hud!: HUD;
  private particleManager!: ParticleManager;
  private combatFeedbackSystem!: CombatFeedbackSystem;
  private controlsText!: Phaser.GameObjects.Text;
  private upgradeSystem!: UpgradeSystem;
  private audioManager!: AudioManager;
  private background!: Phaser.GameObjects.TileSprite;
  private backgroundGrid!: Phaser.GameObjects.TileSprite;
  private chestOpenScreen!: ChestOpenScreen;
  private pauseScreen!: PauseScreen;
  private escapeKey!: Phaser.Input.Keyboard.Key | undefined;
  private diagnosticsOverlay!: DiagnosticsOverlay;
  private mobileInputController!: MobileInputController;
  private mobileControlsOverlay!: MobileControlsOverlay;
  private orientationOverlay!: OrientationOverlay;
  private diagnosticsToggleKey!: Phaser.Input.Keyboard.Key | undefined;
  private noDamageToggleKey!: Phaser.Input.Keyboard.Key | undefined;
  private noDeathToggleKey!: Phaser.Input.Keyboard.Key | undefined;
  private earlyBossToggleKey!: Phaser.Input.Keyboard.Key | undefined;
  private pauseWavesToggleKey!: Phaser.Input.Keyboard.Key | undefined;
  private spawnEnemyHotkey!: Phaser.Input.Keyboard.Key | undefined;
  private giveWeaponHotkey!: Phaser.Input.Keyboard.Key | undefined;
  private skipWaveHotkey!: Phaser.Input.Keyboard.Key | undefined;
  private runtimeTestingMode: RuntimeTestingModeState = DEFAULT_RUNTIME_TESTING_MODE_STATE;
  private effectiveGameSettings!: ReturnType<ContentRegistry['getGameSettings']>;
  private gameplayElapsedSeconds: number = 0;
  private combatPauseStartedAt: number | null = null;
  private diagnosticsLastRefreshTime: number = 0;
  private victoryTriggered: boolean = false;
  private victorySceneStarted: boolean = false;
  private runFinalized: boolean = false;
  private readonly eventUnsubscribers: Array<() => void> = [];
  private readonly keyboardUnsubscribers: Array<() => void> = [];
  private readonly scenePluginUnsubscribers: Array<() => void> = [];
  private readonly viewportRefreshTimers: Array<ReturnType<typeof setTimeout>> = [];
  private contentRegistry!: ContentRegistry;
  private profileRuntime!: ProfileRuntime;
  private profileSnapshot!: PlayerProfile;
  private profileWeaponIds: readonly string[] = [];
  private contentBootstrapError: string | null = null;
  private sceneRuntimeReady: boolean = false;
  private lastDebugCommandResult: DebugCommandResult<unknown> | undefined;
  private visualTextureResolver!: VisualTextureResolver;
  private visualFallbackSummary: TextureFallbackSummary | null = null;
  private readonly logger = createLogger('MainScene');

  constructor() {
    super('Main');
  }

  create() {
    this.logger.debug('create:start');
    this.isPaused = false;
    this.sceneRuntimeReady = false;
    this.lastDebugCommandResult = undefined;
    this.bossesKilled = 0;
    this.killedRequiredBossEncounterIds.clear();
    this.gameplayElapsedSeconds = 0;
    this.combatPauseStartedAt = null;
    this.time.paused = false;
    this.victoryTriggered = false;
    this.victorySceneStarted = false;
    this.runFinalized = false;
    this.physics?.resume();
    this.logger.debug('create:runtime-state-reset', {
      isPaused: this.isPaused
    });

    this.visualFallbackSummary = ensureFallbackTextures(this, undefined, this.logger);
    this.visualTextureResolver = createVisualTextureResolver(this, this.logger);

    // 1.1 Create Infinite Background
    // Screen dimensions
    const width = this.scale.width;
    const height = this.scale.height;

    // Create a TileSprite that covers the entire screen
    this.background = this.add.tileSprite(0, 0, width, height, 'ground')
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(-100); // Send to back
    this.backgroundGrid = this.add.tileSprite(0, 0, width, height, 'ground_grid')
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setAlpha(0.55)
      .setDepth(-99);

    // 2. Создаем шину событий
    this.eventBus = EventBus.getInstance();
    this.runtimeTestingMode = parseRuntimeTestingModeState(this.resolveQueryString(), this.logger);

    if (!this.bootstrapContent()) {
      this.logger.error('create:blocked-by-content-bootstrap', {
        error: this.contentBootstrapError
      });
      this.sceneRuntimeReady = false;
      return;
    }

    this.profileRuntime = getSharedProfileRuntime({ contentRegistry: this.contentRegistry });
    this.profileSnapshot = this.profileRuntime.loadProfile();
    const profileUnlocks = this.profileRuntime.resolveWeaponUnlocks();
    this.profileWeaponIds = profileUnlocks.starterWeaponIds;
    this.logger.info('profile:loaded-for-run', {
      schemaVersion: this.profileSnapshot.schemaVersion,
      totalRuns: this.profileSnapshot.records.totalRuns,
      starterWeaponIds: profileUnlocks.starterWeaponIds,
      availableWeaponIds: profileUnlocks.availableWeaponIds
    });

    this.effectiveGameSettings = applyRuntimeTestingOverrides(
      this.contentRegistry.getGameSettings(),
      this.runtimeTestingMode,
      this.logger
    );
    this.logger.debug('create:runtime-testing-bootstrap', {
      runtimeTestingMode: this.runtimeTestingMode,
      effectiveGameSettings: this.effectiveGameSettings
    });

    // 3. Создаем и инициализируем менеджер пулов
    this.poolManager = new PoolManager();
    const sceneAdapter = new PhaserSceneAdapter(this);
    this.poolManager.init(this);

    // Create pool for floating text
    this.poolManager.registerPool('floating_text', {
      classType: FloatingText,
      maxSize: 50
    });
    // this.poolManager.prefill('floating_text', 50); // Optional if we want to pre-create

    // 4. Гарантируем, что physics-группы урона созданы и подключены к collision routing
    const weaponCollisionPoolTypes = [
      'bullet',
      'orbit_orb',
      'weapon_area',
      'weapon_beam',
      'weapon_boomerang',
      'weapon_chain'
    ];
    weaponCollisionPoolTypes.forEach(type => this.poolManager.registerPhysicsPool(type));

    const weaponCollisionGroups = weaponCollisionPoolTypes
      .map(type => ({ type, group: this.poolManager.getGroup(type) }))
      .filter((entry): entry is { type: string; group: Phaser.GameObjects.Group } => Boolean(entry.group));

    const missingWeaponGroups = weaponCollisionPoolTypes.filter(
      type => !weaponCollisionGroups.some(entry => entry.type === type)
    );

    if (missingWeaponGroups.length > 0) {
      this.logger.warn('create:missing-weapon-collision-groups', { missingWeaponGroups });
    }

    if (weaponCollisionGroups.length === 0) {
      this.logger.error('create:no-weapon-collision-groups');
      this.sceneRuntimeReady = false;
      return;
    }

    this.logger.info('create:weapon-collision-groups-registered', {
      groups: weaponCollisionGroups.map(entry => entry.type)
    });

    // 5. Создаем группу врагов
    this.enemyGroup = this.physics.add.group({
      classType: Phaser.Physics.Arcade.Sprite,
      runChildUpdate: true
    });

    // 6. Создаем адаптер фабрики сущностей
    this.entityFactoryAdapter = new PhaserEntityFactoryAdapter(this, this.contentRegistry, this.visualTextureResolver);

    // Переопределяем создание врагов, чтобы они попадали в группу
    const originalCreateEnemy = this.entityFactoryAdapter.createEnemy.bind(this.entityFactoryAdapter);
    this.entityFactoryAdapter.createEnemy = (x, y, id) => {
      const enemy = originalCreateEnemy(x, y, id);
      if (enemy) this.enemyGroup.add(enemy);
      return enemy;
    };

    // 7. Создаем системы логики
    this.playerStats = new PlayerStats(); // Initialize stats

    // 9. Инициализируем аудио менеджер (Moved up for dependency)
    this.audioManager = new AudioManager(this, this.profileRuntime);
    this.audioManager.setMissingAudioWarningsSuppressed(this.isE2ERuntime());

    this.weaponSystem = new WeaponSystem(this.poolManager, this.audioManager, this.contentRegistry);
    this.mobileInputController = new MobileInputController(this);
    this.mobileInputController.init();
    this.logger.info('mobile-controls:input-initialized', {
      snapshot: this.mobileInputController.getSnapshot()
    });

    // Create Logic Factories
    this.enemyFactory = new EnemyFactory(this.poolManager, sceneAdapter, this.contentRegistry, this.visualTextureResolver);

    this.waveSpawnerSystem = new WaveSpawnerSystem(
      this.enemyFactory,
      this.eventBus,
      sceneAdapter,
      this.contentRegistry,
      this.runtimeTestingMode
    );
    this.bossEncounterSystem = new BossEncounterSystem(
      this.enemyFactory,
      this.eventBus,
      sceneAdapter,
      this.contentRegistry,
      this.runtimeTestingMode,
      (isBossActive, state) => this.waveSpawnerSystem.setBossPauseState(isBossActive, state)
    );
    this.experienceSystem = new ExperienceSystem(this.eventBus, this.contentRegistry.getGameSettings().experience);
    this.lootSystem = new LootSystem(this.poolManager, this.eventBus, this.contentRegistry);
    this.upgradeSystem = new UpgradeSystem(this.weaponSystem, this.playerStats, this.contentRegistry, {
      availableWeaponIds: profileUnlocks.availableWeaponIds,
      getCurrentHealth: () => Number(this.player.getData('health'))
    });

    // 8. Инициализируем игровые сущности
    this.initializeGameSystems();

    // 10. Инициализируем систему коллизий
    this.collisionSystem = new CollisionSystem(
      this,
      this.player,
      this.enemyGroup,
      weaponCollisionGroups.map(entry => entry.group),
      this.poolManager,
      this.audioManager,
      this.runtimeTestingMode
    );

    // ...
    // 10. UI & Event Listeners
    this.levelUpScreen = new LevelUpScreen(this, 0, 0);
    this.add.existing(this.levelUpScreen);
    this.levelUpScreen.setScrollFactor(0); // Fix to camera

    // Initialize Particle Manager
    this.particleManager = new ParticleManager(this);
    this.combatFeedbackSystem = new CombatFeedbackSystem(
      this,
      this.eventBus,
      this.particleManager,
      this.audioManager,
      this.runtimeTestingMode,
      this.effectiveGameSettings.combatFeedback
    );

    // Initialize ChestOpenScreen
    this.chestOpenScreen = new ChestOpenScreen(this);
    const unsubscribeVisualSettings = this.profileRuntime.onSettingsChanged(settings => {
      this.combatFeedbackSystem.setVisualSettings(settings.gameplay);
      this.collisionSystem?.setReducedFlashes(settings.gameplay.reduceFlashes === true);
      this.chestOpenScreen.setReducedFlashes(settings.gameplay.reduceFlashes === true);
      this.levelUpScreen.setReducedFlashes(settings.gameplay.reduceFlashes === true);
    });
    this.events.once('shutdown', unsubscribeVisualSettings);

    // Initialize PauseScreen
    this.pauseScreen = new PauseScreen(this, this.audioManager, this.profileRuntime);

    const declaredCombatSfx = Object.values(this.effectiveGameSettings.combatFeedback)
      .map(effect => effect?.sound?.key)
      .filter((key): key is string => typeof key === 'string' && key.length > 0);
    this.audioManager.registerDeclaredOptionalSfxKeys(declaredCombatSfx);

    if (this.input && this.input.keyboard) {
        this.escapeKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        
        this.bindKeyDown(this.escapeKey, () => {
            // Can only pause/unpause with ESC if we are not in Level Up or Chest screens
            if (this.levelUpScreen.visible || this.chestOpenScreen.visible) return;

            if (this.isPaused) {
                // If it's already paused, check if PauseScreen is visible
                if (this.pauseScreen.visible) {
                    this.pauseScreen.hide(); // EventBus 'game:resume' will handle unpausing
                }
            } else {
                // Pause the game and show the menu
                this.eventBus.publish({ type: 'game:pause', data: { reason: 'user' } });
                this.pauseScreen.show(this.weaponSystem, this.upgradeSystem, this.playerStats);
            }
        });
    }

    this.setupEventListeners();

    // HUD
    this.hud = new HUD(this, 0, 0);
    this.add.existing(this.hud);
    // Note: HUD expects ExperienceSystem interface which matches our class structure mostly
    // We cast to any to avoid strict type mismatch if interfaces slightly differ in this context
    this.hud.init(this, this.player as Phaser.Types.Physics.Arcade.SpriteWithDynamicBody, this.experienceSystem as any);
    this.hud.setScrollFactor(0); // Fix HUD to screen
    this.hud.setDepth(100); // Ensure HUD is above game objects but below LevelUpScreen (1000)
    // Initialize stats display
    // Initialize stats display
    if (this.playerStats) {
      this.hud.updateStats(this.playerStats);
      this.hud.updateHealth(this.player.getData('health') || 100, this.playerStats.get('maxHealth'));
      this.hud.updateXP(this.experienceSystem['currentXp'], this.experienceSystem['xpToNextLevel']);
      this.hud.updateInventory(this.upgradeSystem.getInventorySnapshot());
    }

    // Controls Help
    const loc = LocalizationManager.getInstance();
    this.controlsText = this.add.text(10, this.scale.height - 30, loc.getText('ui.controls'), {
      font: '16px Arial',
      color: '#ffffff'
    });
    this.controlsText.setScrollFactor(0);
    this.controlsText.setDepth(100);

    this.diagnosticsOverlay = new DiagnosticsOverlay(this);
    this.mobileControlsOverlay = new MobileControlsOverlay(this);
    this.orientationOverlay = new OrientationOverlay(this);
    this.orientationOverlay.attemptLandscapeLock();
    this.bindScaleEvent(resolveScaleEvent('RESIZE', 'resize'), () => {
      this.resizeSceneViewport();
      this.mobileControlsOverlay?.resize();
      this.orientationOverlay?.resize();
    });
    this.bindScaleEvent(resolveScaleEvent('ORIENTATION_CHANGE', 'orientationchange'), () => {
      this.logger.debug('orientation:changed', this.orientationOverlay?.getSnapshot(this.mobileInputController?.getSnapshot().touchCapable ?? false));
      this.refreshViewportAfterBrowserResize('orientationchange');
    });
    this.bindScaleEvent(resolveScaleEvent('ENTER_FULLSCREEN', 'enterfullscreen'), () => {
      this.orientationOverlay?.handleFullscreenEntered();
      this.refreshViewportAfterBrowserResize('enterfullscreen');
    });
    this.bindScaleEvent(resolveScaleEvent('LEAVE_FULLSCREEN', 'leavefullscreen'), () => {
      this.orientationOverlay?.handleFullscreenLeft();
      this.refreshViewportAfterBrowserResize('leavefullscreen');
    });
    this.bindScaleEvent(resolveScaleEvent('FULLSCREEN_FAILED', 'fullscreenfailed'), (reason) => {
      this.orientationOverlay?.handleFullscreenFailed(reason);
    });
    this.bindScaleEvent(resolveScaleEvent('FULLSCREEN_UNSUPPORTED', 'fullscreenunsupported'), (reason) => {
      this.orientationOverlay?.handleFullscreenUnsupported(reason);
    });
    this.setDiagnosticsOverlayVisibility(this.runtimeTestingMode.enabled && this.runtimeTestingMode.diagnosticsOverlay, 'create');
    this.setupTestingInputBindings();
    // Пересчитываем интерфейс после замены объектов предыдущего забега.
    this.resizeSceneViewport();
    this.sceneRuntimeReady = true;

    this.logger.info('create:complete');

    // Start Level Music
    this.audioManager.playMusic('level_theme', { volume: 0.3 });

    this.events?.once('shutdown', this.cleanupSceneRuntime, this);
  }

  public getContentBootstrapState(): { blocked: boolean; error: string | null } {
    return {
      blocked: this.contentBootstrapError !== null,
      error: this.contentBootstrapError
    };
  }

  public async runWeaponDamageSmoke(weaponId: string): Promise<WeaponDamageSmokeResult> {
    if (!this.player || !this.poolManager || !this.enemyGroup) {
      return { weaponId, appeared: false, damaged: false, startHealth: 0, endHealth: 0, error: 'main-scene-not-ready' };
    }

    const weapon = this.contentRegistry.getWeapon(weaponId);
    const level = weapon?.levels.find(entry => entry.level === 1) ?? weapon?.levels[0];
    if (!weapon || !level) {
      return { weaponId, appeared: false, damaged: false, startHealth: 0, endHealth: 0, error: 'weapon-not-found' };
    }

    const runtimeConfig = createWeaponRuntimeConfig(weapon, level, this.logger);
    const poolType = runtimeConfig ? this.getWeaponSmokePoolType(runtimeConfig.type) : undefined;
    if (!runtimeConfig || !poolType) {
      return { weaponId, appeared: false, damaged: false, startHealth: 0, endHealth: 0, error: 'unsupported-weapon-runtime' };
    }

    const x = this.player.x + 96;
    const y = this.player.y;
    const enemy = this.entityFactoryAdapter.createEnemy(x, y, 'basic_enemy') as Phaser.Physics.Arcade.Sprite | null;
    if (!enemy) {
      return { weaponId, appeared: false, damaged: false, startHealth: 0, endHealth: 0, poolType, error: 'enemy-spawn-failed' };
    }

    this.enemyGroup.add(enemy);
    enemy.setPosition(x, y);
    enemy.setData('speed', 0);
    enemy.setVelocity(0, 0);
    if (enemy.body) {
      (enemy.body as Phaser.Physics.Arcade.Body).enable = true;
      (enemy.body as Phaser.Physics.Arcade.Body).reset(x, y);
    }

    const projectile = this.poolManager.spawnProjectile({ x, y, type: poolType }) as Phaser.Physics.Arcade.Sprite | null;
    if (!projectile?.body) {
      return { weaponId, appeared: false, damaged: false, startHealth: 0, endHealth: 0, poolType, error: 'projectile-spawn-failed' };
    }

    const damage = runtimeConfig.damage.baseDamage + runtimeConfig.damage.bonusDamage;
    const startHealth = Number(enemy.getData('health') ?? enemy.getData('maxHealth') ?? 0);
    projectile.setData('damage', damage);
    projectile.setData('pierce', 999);
    projectile.setData('isOrbit', true);
    projectile.setData('hitList', []);
    projectile.setData('weaponType', runtimeConfig.type);
    if (runtimeConfig.type === 'chain') { projectile.setData('chainTarget', enemy); projectile.setData('chainFrom', {x: this.player.x, y: this.player.y}); }
    projectile.setData('damagePayload', runtimeConfig.damage);
    projectile.setData('areaRadius', runtimeConfig.projectile.area);
    projectile.setVisible(true);
    projectile.setActive(true);
    (projectile.body as Phaser.Physics.Arcade.Body).enable = true;
    (projectile.body as Phaser.Physics.Arcade.Body).reset(x, y);

    if (runtimeConfig.type === 'area') {
      projectile.setDisplaySize(Math.max(24, runtimeConfig.projectile.area), Math.max(24, runtimeConfig.projectile.area));
    } else if (runtimeConfig.type === 'beam') {
      projectile.setDisplaySize(Math.max(32, runtimeConfig.projectile.range ?? 160), 8);
    }

    this.collisionSystem.handleWeaponEnemySmokeCollision(projectile, enemy);
    await new Promise<void>(resolve => this.time.delayedCall(60, resolve));

    const endHealth = Number(enemy.getData('health') ?? 0);
    const appeared = projectile.active && projectile.visible;
    const damaged = Number.isFinite(startHealth) && Number.isFinite(endHealth) && endHealth < startHealth;

    projectile.setActive(false).setVisible(false);
    enemy.setActive(false).setVisible(false);

    return { weaponId, appeared, damaged, startHealth, endHealth, poolType };
  }

  public runProjectileCountSmokeForE2E(weaponId: string, targetLevel: number) {
    if (!this.isE2ERuntime() || !this.scene.isActive()) return null;
    if (!['basic_shot', 'boomerang_blade'].includes(weaponId) || !Number.isInteger(targetLevel) || targetLevel < 2 || targetLevel > 5) return null;
    if (!this.giveWeaponForDebug({ weaponId, targetLevel, source: 'e2e' }).ok) return null;
    const weapon = this.contentRegistry.getWeapon(weaponId);
    const group = weapon && this.poolManager.getGroup(this.getWeaponSmokePoolType(weapon.type)!);
    if (!group) return null;
    this.weaponSystem.update(4_000);
    const previous = new Set(group.getChildren().filter(sprite => sprite.active));
    this.weaponSystem.fire();
    const projectiles = group.getChildren().filter(sprite => sprite.active && !previous.has(sprite)) as Phaser.Physics.Arcade.Sprite[];
    try {
      return projectiles.map(sprite => ({
        x: sprite.x, y: sprite.y,
        vx: (sprite.body as Phaser.Physics.Arcade.Body).velocity.x,
        vy: (sprite.body as Phaser.Physics.Arcade.Body).velocity.y,
        visible: sprite.active && sprite.visible,
        texture: sprite.texture.key
      }));
    } finally {
      projectiles.forEach(sprite => this.poolManager.despawn(sprite));
    }
  }

  public async runTintSmokeForE2E() {
    if (!this.isE2ERuntime() || !this.scene.isActive()) return null;
    if (this.ensureDebugCommandAllowed('getDebugStatsSnapshot', 'e2e', true)) return null;
    const enemy = this.entityFactoryAdapter.createEnemy(this.player.x + 96, this.player.y, 'basic_enemy') as Phaser.Physics.Arcade.Sprite;
    if (!enemy) return null;
    const colors = [0x4477aa, 0x5588bb, 0x6699cc, 0x77aadd] as const;
    enemy.setTint(...colors);
    try {
      for (let index = 0; index < 2; index++) {
        const projectile = this.poolManager.spawnProjectile({ x: enemy.x, y: enemy.y, type: 'bullet' }) as Phaser.Physics.Arcade.Sprite;
        projectile.setData({ damage: 1, damagePayload: undefined, weaponType: 'projectile', pierce: 1, isOrbit: false, hitList: [] });
        this.collisionSystem.handleWeaponEnemySmokeCollision(projectile, enemy);
        this.poolManager.despawn(projectile);
      }
      const flashed = enemy.tintTopLeft;
      await new Promise<void>(resolve => this.time.delayedCall(70, resolve));
      return { colors, flashed, restored: [enemy.tintTopLeft, enemy.tintTopRight, enemy.tintBottomLeft, enemy.tintBottomRight], health: Number(enemy.getData('health')) };
    } finally {
      enemy.destroy();
    }
  }

  public showUpgradeChoicesForE2E(): boolean {
    if (!this.isE2ERuntime() || !this.scene.isActive()) return false;
    if (this.ensureDebugCommandAllowed('getDebugStatsSnapshot', 'e2e', true)) return false;
    const choices = this.upgradeSystem.getUpgradeChoices(100).filter(choice => choice.icon && this.textures.exists(choice.icon)).slice(0, 3);
    if (!choices.length) return false;
    this.eventBus.publish({ type: 'game:pause', data: { reason: 'user' } });
    this.levelUpScreen.showChoices(choices);
    return true;
  }

  public getUpgradeChoicesSnapshotForE2E() {
    if (!this.isE2ERuntime() || !this.scene.isActive()) return null;
    if (this.ensureDebugCommandAllowed('getDebugStatsSnapshot', 'e2e', false)) return null;
    return {
      visible: this.levelUpScreen.visible, paused: this.isPaused, width: this.scale.width, height: this.scale.height,
      cards: this.levelUpScreen.list.filter((child): child is Phaser.GameObjects.Container => child instanceof Phaser.GameObjects.Container && Boolean(child.input))
        .map(card => {
          const icon = card.list.find((child): child is Phaser.GameObjects.Sprite => child instanceof Phaser.GameObjects.Sprite);
          const backgrounds = card.list.filter(child => child instanceof Phaser.GameObjects.Graphics || child instanceof Phaser.GameObjects.Rectangle);
          return {
            x: card.x, y: card.y, alpha: card.alpha,
            text: card.list.filter((child): child is Phaser.GameObjects.Text => child instanceof Phaser.GameObjects.Text)
              .map(child => child.text).join('\n'),
            hovered: (backgrounds[1] as Phaser.GameObjects.Graphics | undefined)?.visible ?? false,
            iconOnTop: Boolean(icon && backgrounds.every(background => card.list.indexOf(background) < card.list.indexOf(icon))),
            texture: icon?.texture.key ?? null,
            hasTexture: Boolean(icon?.texture.source[0]?.image),
            iconBounds: icon?.getBounds() ?? null
          };
        })
    };
  }

  public forceNextBossEncounterForE2E(): {
    spawned: boolean;
    encounterId?: string;
    reason?: 'testing-disabled' | 'active-encounter' | 'no-remaining-encounters';
  } {
    if (!this.runtimeTestingMode.enabled) {
      return { spawned: false, reason: 'testing-disabled' };
    }

    return this.bossEncounterSystem.forceSpawnNextEncounterForTesting();
  }

  public getActiveBossEncounterForE2E(): ActiveBossEncounterE2ESnapshot | null {
    const bossEncounterState = this.bossEncounterSystem.getDiagnosticsSnapshot();
    const activeEncounterId = bossEncounterState.activeEncounterId;
    if (!activeEncounterId) {
      return null;
    }

    const activeBossSprite = this.findActiveBossSpriteByEncounterId(activeEncounterId);
    if (!activeBossSprite) {
      return null;
    }

    const metadata = activeBossSprite.getData('bossEncounterMetadata') as {
      encounterId: string;
      enemyId: string;
      displayName?: string;
      requiredForVictory?: boolean;
    } | undefined;
    if (!metadata) {
      return null;
    }

    return {
      encounterId: metadata.encounterId,
      enemyId: metadata.enemyId,
      displayName: metadata.displayName ?? metadata.enemyId,
      currentHealth: Number(activeBossSprite.getData('health') ?? 0),
      maxHealth: Number(activeBossSprite.getData('maxHealth') ?? 0),
      requiredForVictory: metadata.requiredForVictory !== false
    };
  }

  public killActiveBossForE2E(): { killed: boolean; encounterId?: string; reason?: 'no-active-encounter' | 'boss-sprite-missing' } {
    const bossEncounterState = this.bossEncounterSystem.getDiagnosticsSnapshot();
    const activeEncounterId = bossEncounterState.activeEncounterId;
    if (!activeEncounterId) {
      return { killed: false, reason: 'no-active-encounter' };
    }

    const activeBossSprite = this.findActiveBossSpriteByEncounterId(activeEncounterId);
    if (!activeBossSprite) {
      return { killed: false, reason: 'boss-sprite-missing' };
    }

    (this.collisionSystem as unknown as { resolveEnemyDeath?: (enemy: Phaser.Physics.Arcade.Sprite, remainingHealth: number) => void })
      .resolveEnemyDeath?.(activeBossSprite, 0);

    return { killed: true, encounterId: activeEncounterId };
  }

  public runRewardSmokeForE2E(mode: 'ordinary' | 'evolution'): RewardSmokeResult {
    if (!this.isE2ERuntime() || !this.scene.isActive()) return { ok: false, reason: 'scene-not-ready' };
    const guard = this.ensureDebugCommandAllowed('getDebugStatsSnapshot', 'e2e', true);
    if (guard && !guard.ok) return { ok: false, reason: guard.reason };
    if (mode !== 'ordinary' && mode !== 'evolution') return { ok: false, reason: 'invalid-payload' };

    this.logger.debug('runRewardSmokeForE2E:start', { mode });
    if (mode === 'evolution') {
      const recipe = this.contentRegistry.findEvolutionRecipe('basic_shot');
      const weapon = this.contentRegistry.getWeapon('basic_shot');
      if (!recipe || !weapon) return { ok: false, reason: 'reward-unavailable' };
      const grant = this.giveWeaponForDebug({ weaponId: weapon.id, targetLevel: weapon.levels.length, source: 'e2e' });
      if (!grant.ok) return { ok: false, reason: grant.reason };

      const passiveLevel = this.upgradeSystem.getAcquiredPassives().find(passive => passive.id === recipe.requiredPassiveId)?.level ?? 0;
      const fullChoiceCount = this.contentRegistry.getWeapons().length + this.contentRegistry.getPassives().length;
      for (let level = passiveLevel; level < recipe.requiredPassiveLevel; level += 1) {
        const choice = this.upgradeSystem.getUpgradeChoices(fullChoiceCount)
          .find(option => option.passiveId === recipe.requiredPassiveId);
        if (!choice || !this.upgradeSystem.applyUpgrade(choice)) return { ok: false, reason: 'reward-unavailable' };
      }
    }

    if (Boolean(this.upgradeSystem.checkEvolutionAvailability()) !== (mode === 'evolution')) {
      return { ok: false, reason: 'reward-unavailable' };
    }
    const aliveParticles = () => this.children.list.reduce((count, child) => (
      child instanceof Phaser.GameObjects.Particles.ParticleEmitter ? count + child.getAliveParticleCount() : count
    ), 0);
    const inventoryBefore = this.weaponSystem.getActiveWeapons().map(weapon => weapon.id);
    const beforeOpening = aliveParticles();
    this.eventBus.publish({ type: 'chest:opened' });
    const afterOpening = aliveParticles();
    const button = (this.chestOpenScreen as unknown as { button: Phaser.GameObjects.Container }).button;
    const position = button.getWorldTransformMatrix();
    button.emit('pointerdown');
    button.emit('pointerup');
    const afterReward = aliveParticles();
    button.emit('pointerdown');
    button.emit('pointerup');
    const result: RewardSmokeResult = {
      ok: true,
      mode,
      particleDeltas: {
        opening: afterOpening - beforeOpening,
        reward: afterReward - afterOpening,
        repeatedClose: aliveParticles() - afterReward
      },
      inventoryBefore,
      inventoryAfter: this.weaponSystem.getActiveWeapons().map(weapon => weapon.id),
      collectButton: { x: position.tx, y: position.ty },
      visibleAfterClose: this.chestOpenScreen.visible
    };
    this.logger.debug('runRewardSmokeForE2E:complete', result);
    return result;
  }

  public triggerCombatFeedbackSmokeForE2E(): CombatFeedbackSmokeResult {
    return this.combatFeedbackSystem.triggerDeterministicSmoke();
  }

  public runCombatLifecycleSmokeForE2E() {
    if (!this.isE2ERuntime() || !this.scene.isActive() || this.isPaused) return null;
    const before = this.combatFeedbackSystem.getDiagnosticsSnapshot().deaths;
    const direct = this.enemyFactory.create('basic_enemy', this.player.x + 200, this.player.y)!;
    const sprite = direct.getSprite();
    this.enemyGroup.add(sprite);
    const projectile = this.poolManager.spawnProjectile({ x: sprite.x, y: sprite.y, type: 'bullet' });
    if (!projectile) return null;
    projectile.setData('damage', 1000).setData('weaponType', 'projectile').setData('isOrbit', false).setData('hitList', []).setData('pierce', 1);
    this.collisionSystem.handleWeaponEnemySmokeCollision(projectile, sprite);
    const directInactive = !sprite.active && !sprite.body.enable;
    const burned = this.enemyFactory.create('basic_enemy', this.player.x + 220, this.player.y)!;
    const burnedSprite = burned.getSprite();
    this.enemyGroup.add(burnedSprite);
    burned.applyStatusEffects({ burn: { damagePerSecond: 1000, expiresAt: this.time.now + 1000 } });
    burned.update(1, this.player);
    return { directInactive, burnInactive: !burnedSprite.active && !burnedSprite.body.enable, deathEvents: this.combatFeedbackSystem.getDiagnosticsSnapshot().deaths - before, sameSpriteReused: sprite === burnedSprite };
  }

  public spawnEnemyForDebug(payload: SpawnEnemyDebugPayload = {}): DebugCommandResult<SpawnEnemyDebugResult> {
    const source = resolveDebugCommandSource(payload.source);
    const command = 'spawnEnemy' as const;
    this.logger.debug('spawnEnemyForDebug:input', { payload, source });

    const guardFailure = this.ensureDebugCommandAllowed(command, source, true);
    if (guardFailure) {
      return this.rememberDebugCommandResult(guardFailure);
    }

    const enemyId = payload.enemyId ?? 'basic_enemy';
    if (!this.contentRegistry.findEnemy(enemyId)) {
      const failure = createDebugCommandFailure(command, source, 'unknown-enemy', 'Unknown enemy ID.', { enemyId });
      this.logger.warn('spawnEnemyForDebug:unknown-enemy', { source, enemyId });
      return this.rememberDebugCommandResult(failure);
    }

    if (payload.x !== undefined && !Number.isFinite(payload.x)) {
      const failure = createDebugCommandFailure(command, source, 'invalid-payload', 'x must be a finite number.', {
        x: payload.x
      });
      this.logger.warn('spawnEnemyForDebug:invalid-x', { source, x: payload.x });
      return this.rememberDebugCommandResult(failure);
    }

    if (payload.y !== undefined && !Number.isFinite(payload.y)) {
      const failure = createDebugCommandFailure(command, source, 'invalid-payload', 'y must be a finite number.', {
        y: payload.y
      });
      this.logger.warn('spawnEnemyForDebug:invalid-y', { source, y: payload.y });
      return this.rememberDebugCommandResult(failure);
    }

    const usedDefaultCoordinates = payload.x === undefined || payload.y === undefined;
    const x = payload.x ?? (this.player.x + 128);
    const y = payload.y ?? this.player.y;

    try {
      const createdEnemy = this.enemyFactory.create(enemyId, x, y);
      if (!createdEnemy) {
        const failure = createDebugCommandFailure(command, source, 'execution-failed', 'Enemy creation returned null.', {
          enemyId,
          x,
          y
        });
        this.logger.warn('spawnEnemyForDebug:create-returned-null', { source, enemyId, x, y });
        return this.rememberDebugCommandResult(failure);
      }

      const successData = {
        enemyId,
        x,
        y,
        activeEnemyCount: this.countActiveEnemies(),
        usedDefaultCoordinates
      };
      const result = createDebugCommandSuccess(command, source, successData);
      this.logger.info('spawnEnemyForDebug:accepted', {
        source,
        enemyId,
        x,
        y,
        activeEnemyCount: successData.activeEnemyCount
      });
      return this.rememberDebugCommandResult(result);
    } catch (error) {
      const failure = createDebugCommandFailure(command, source, 'execution-failed', 'Unexpected error while spawning enemy.', {
        enemyId,
        x,
        y,
        error: error instanceof Error ? error.message : String(error)
      });
      this.logger.error('spawnEnemyForDebug:execution-failed', {
        source,
        enemyId,
        x,
        y,
        error
      });
      return this.rememberDebugCommandResult(failure);
    }
  }

  public giveWeaponForDebug(payload: GiveWeaponDebugPayload = {}): DebugCommandResult<unknown> {
    const source = resolveDebugCommandSource(payload.source);
    const command = 'giveWeapon' as const;
    this.logger.debug('giveWeaponForDebug:input', { payload, source });

    const guardFailure = this.ensureDebugCommandAllowed(command, source, true);
    if (guardFailure) {
      return this.rememberDebugCommandResult(guardFailure);
    }

    if (payload.targetLevel !== undefined && (!Number.isInteger(payload.targetLevel) || payload.targetLevel < 1)) {
      const failure = createDebugCommandFailure(command, source, 'invalid-payload', 'targetLevel must be a positive integer.', {
        targetLevel: payload.targetLevel
      });
      this.logger.warn('giveWeaponForDebug:invalid-target-level', {
        source,
        targetLevel: payload.targetLevel
      });
      return this.rememberDebugCommandResult(failure);
    }

    const result = this.upgradeSystem.grantWeaponForTesting({
      ...payload,
      source
    });
    if (result.ok) {
      this.refreshAfterUpgrade('debug-command');
      this.logger.info('giveWeaponForDebug:accepted', {
        source,
        weaponId: result.data.weaponId,
        currentLevel: result.data.currentLevel,
        targetLevel: result.data.targetLevel,
        unlocked: result.data.unlocked
      });
    } else {
      if (result.reason === 'invalid-payload' || result.reason === 'execution-failed' || result.reason === 'unknown-weapon') {
        this.logger.warn('giveWeaponForDebug:rejected', {
          source,
          reason: result.reason,
          message: result.message,
          details: result.details
        });
      } else {
        this.logger.debug('giveWeaponForDebug:rejected', {
          source,
          reason: result.reason,
          message: result.message,
          details: result.details
        });
      }
    }
    return this.rememberDebugCommandResult(result);
  }

  public skipWaveForDebug(payload: SkipWaveDebugPayload = {}): DebugCommandResult<WaveDebugResult> {
    const source = resolveDebugCommandSource(payload.source);
    const command = 'skipWave' as const;
    this.logger.debug('skipWaveForDebug:input', { payload, source });

    const guardFailure = this.ensureDebugCommandAllowed(command, source, true);
    if (guardFailure) {
      return this.rememberDebugCommandResult(guardFailure);
    }

    const result = this.waveSpawnerSystem.skipToNextWaveForTesting({ source });
    if (result.ok) {
      this.logger.info('skipWaveForDebug:accepted', {
        source,
        previousWaveIndex: result.data.previousWaveIndex,
        nextWaveIndex: result.data.nextWaveIndex
      });
    } else {
      if (result.reason === 'invalid-payload' || result.reason === 'execution-failed' || result.reason === 'wave-index-out-of-range') {
        this.logger.warn('skipWaveForDebug:rejected', {
          source,
          reason: result.reason
        });
      } else {
        this.logger.debug('skipWaveForDebug:rejected', {
          source,
          reason: result.reason
        });
      }
    }
    return this.rememberDebugCommandResult(result);
  }

  public forceWaveForDebug(payload?: ForceWaveDebugPayload): DebugCommandResult<WaveDebugResult> {
    const source = resolveDebugCommandSource(payload?.source);
    const command = 'forceWave' as const;
    this.logger.debug('forceWaveForDebug:input', { payload, source });

    const guardFailure = this.ensureDebugCommandAllowed(command, source, true);
    if (guardFailure) {
      return this.rememberDebugCommandResult(guardFailure);
    }

    if (!payload || typeof payload !== 'object') {
      const failure = createDebugCommandFailure(command, source, 'invalid-payload', 'forceWave requires a payload with waveIndex.');
      this.logger.warn('forceWaveForDebug:invalid-payload', { source, payload });
      return this.rememberDebugCommandResult(failure);
    }

    const result = this.waveSpawnerSystem.forceWaveForTesting({
      ...payload,
      source
    });
    if (result.ok) {
      this.logger.info('forceWaveForDebug:accepted', {
        source,
        previousWaveIndex: result.data.previousWaveIndex,
        nextWaveIndex: result.data.nextWaveIndex
      });
    } else {
      if (result.reason === 'invalid-payload' || result.reason === 'execution-failed' || result.reason === 'wave-index-out-of-range') {
        this.logger.warn('forceWaveForDebug:rejected', {
          source,
          reason: result.reason
        });
      } else {
        this.logger.debug('forceWaveForDebug:rejected', {
          source,
          reason: result.reason
        });
      }
    }
    return this.rememberDebugCommandResult(result);
  }

  public getDebugStatsSnapshot(payload: DebugStatsSnapshotPayload = {}): DebugCommandResult<DebugStatsSnapshot> {
    const source = resolveDebugCommandSource(payload.source);
    const command = 'getDebugStatsSnapshot' as const;
    this.logger.debug('getDebugStatsSnapshot:input', { source });

    const guardFailure = this.ensureDebugCommandAllowed(command, source, false);
    if (guardFailure) {
      return this.rememberDebugCommandResult(guardFailure);
    }

    const waveSnapshot = this.waveSpawnerSystem.getDiagnosticsSnapshot();
    const inventory = this.upgradeSystem.getInventorySnapshot();
    const successData: DebugStatsSnapshot = {
      gameplayElapsedSeconds: this.gameplayElapsedSeconds,
      experience: {
        level: this.experienceSystem.getLevel(),
        currentXp: this.experienceSystem.getCurrentXp(),
        xpToNextLevel: this.experienceSystem.getXpToNextLevel()
      },
      playerStats: this.playerStats.getSnapshot(),
      health: {
        current: Number(this.player.getData('health') ?? 0),
        max: this.playerStats.get('maxHealth')
      },
      inventory: {
        activeWeaponCount: inventory.weapons.length,
        maxWeapons: inventory.maxWeapons,
        activePassiveCount: inventory.passives.length,
        maxPassives: inventory.maxPassives,
        weapons: inventory.weapons.map(weapon => ({
          id: weapon.id,
          level: weapon.level,
          maxLevel: weapon.maxLevel
        })),
        passives: inventory.passives.map(passive => ({
          id: passive.id,
          level: passive.level,
          maxLevel: passive.maxLevel
        }))
      },
      activeEnemyCount: this.countActiveEnemies(),
      wave: {
        currentWaveIndex: waveSnapshot.currentWaveIndex,
        elapsedWaveTime: waveSnapshot.elapsedWaveTime,
        wavesStarted: waveSnapshot.wavesStarted,
        elapsedRunTime: waveSnapshot.elapsedRunTime,
        bossActive: waveSnapshot.bossActive,
        bossesSpawned: waveSnapshot.bossesSpawned,
        pauseWaves: waveSnapshot.pauseWaves
      },
      lastCommandResult: this.lastDebugCommandResult
    };
    const result = createDebugCommandSuccess(command, source, successData);
    this.logger.debug('getDebugStatsSnapshot:result', {
      source,
      activeEnemyCount: successData.activeEnemyCount,
      waveIndex: successData.wave.currentWaveIndex
    });
    return this.rememberDebugCommandResult(result);
  }

  private getWeaponSmokePoolType(type: WeaponBehaviorType): string | undefined {
    switch (type) {
      case 'area':
        return 'weapon_area';
      case 'beam':
        return 'weapon_beam';
      case 'boomerang':
        return 'weapon_boomerang';
      case 'chain':
        return 'weapon_chain';
      case 'projectile':
        return 'bullet';
      case 'orbit':
        return 'orbit_orb';
      default:
        return undefined;
    }
  }

  private findActiveBossSpriteByEncounterId(encounterId: string): Phaser.Physics.Arcade.Sprite | null {
    const children = this.enemyGroup?.getChildren() ?? [];
    for (const child of children) {
      const sprite = child as Phaser.Physics.Arcade.Sprite;
      if (!sprite.active) {
        continue;
      }

      const metadata = sprite.getData('bossEncounterMetadata') as { encounterId?: string } | undefined;
      if (metadata?.encounterId === encounterId) {
        return sprite;
      }
    }

    return null;
  }

  private ensureDebugCommandAllowed(
    command: 'spawnEnemy' | 'giveWeapon' | 'skipWave' | 'forceWave' | 'getDebugStatsSnapshot',
    source: DebugCommandSource,
    stateChanging: boolean
  ): DebugCommandResult<never> | null {
    this.logger.debug('ensureDebugCommandAllowed:check', {
      command,
      source,
      stateChanging,
      testingEnabled: this.runtimeTestingMode.enabled,
      sceneRuntimeReady: this.sceneRuntimeReady
    });

    if (!this.runtimeTestingMode.enabled) {
      return createDebugCommandFailure(command, source, 'testing-disabled');
    }

    if (!this.sceneRuntimeReady || !this.isSceneReadyForDebugCommands()) {
      return createDebugCommandFailure(command, source, 'scene-not-ready');
    }

    if (stateChanging && this.isDebugCommandBlockedByOverlay()) {
      return createDebugCommandFailure(command, source, 'blocked-by-overlay');
    }

    return null;
  }

  private isSceneReadyForDebugCommands(): boolean {
    return Boolean(
      this.player
      && this.enemyGroup
      && this.weaponSystem
      && this.upgradeSystem
      && this.waveSpawnerSystem
      && this.playerStats
      && this.contentRegistry
      && this.profileRuntime
      && !this.contentBootstrapError
      && !this.victorySceneStarted
      && !this.runFinalized
    );
  }

  private isDebugCommandBlockedByOverlay(): boolean {
    if (this.levelUpScreen?.visible || this.chestOpenScreen?.visible || this.pauseScreen?.visible) {
      return true;
    }

    if (this.isPaused || this.victoryTriggered || this.victorySceneStarted || this.runFinalized) {
      return true;
    }

    return false;
  }

  private countActiveEnemies(): number {
    return this.enemyGroup.getChildren().filter(child => Boolean((child as { active?: boolean }).active)).length;
  }

  private rememberDebugCommandResult<T>(result: DebugCommandResult<T>): DebugCommandResult<T> {
    if (this.runtimeTestingMode.enabled && result.command !== 'getDebugStatsSnapshot') {
      this.lastDebugCommandResult = result as DebugCommandResult<unknown>;
    }

    return result;
  }

  private bootstrapContent(): boolean {
    this.logger.debug('bootstrapContent:start');

    try {
      this.contentRegistry = loadContentRegistry();
      this.contentBootstrapError = null;
      this.logger.info('bootstrapContent:complete', this.contentRegistry.getSummary());
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.contentBootstrapError = message;
      this.logger.error('bootstrapContent:failed', { message, error });
      this.showContentBootstrapError(message);
      return false;
    }
  }

  private showContentBootstrapError(message: string): void {
    this.add.text(this.scale.width / 2, this.scale.height / 2, `Content bootstrap failed\n${message}`, {
      font: '20px Arial',
      color: '#ff6666',
      align: 'center',
      wordWrap: { width: Math.max(280, this.scale.width - 80) }
    }).setOrigin(0.5).setScrollFactor(0).setDepth(3000);
  }

  private resolveQueryString(): string {
    const search = (globalThis as { location?: { search?: string } }).location?.search;
    return typeof search === 'string' ? search : '';
  }

  private isE2ERuntime(): boolean {
    return new URLSearchParams(this.resolveQueryString()).has('e2e');
  }

  private setupTestingInputBindings(): void {
    if (!this.runtimeTestingMode.enabled) {
      this.logger.debug('setupTestingInputBindings:skipped-testing-disabled');
      return;
    }

    if (!this.input?.keyboard) {
      this.logger.warn('setupTestingInputBindings:keyboard-unavailable');
      return;
    }

    this.diagnosticsToggleKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F1);
    this.noDamageToggleKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F2);
    this.noDeathToggleKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F3);
    this.earlyBossToggleKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F4);
    this.pauseWavesToggleKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F5);
    this.spawnEnemyHotkey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F6);
    this.giveWeaponHotkey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F7);
    this.skipWaveHotkey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F8);

    this.bindKeyDown(this.diagnosticsToggleKey, () => {
      this.applyRuntimeTestingModePatch({
        diagnosticsOverlay: !this.runtimeTestingMode.diagnosticsOverlay,
        enabled: true
      }, 'hotkey:F1');
    });
    this.bindKeyDown(this.noDamageToggleKey, () => {
      this.applyRuntimeTestingModePatch({
        disablePlayerDamage: !this.runtimeTestingMode.disablePlayerDamage,
        enabled: true
      }, 'hotkey:F2');
    });
    this.bindKeyDown(this.noDeathToggleKey, () => {
      this.applyRuntimeTestingModePatch({
        disablePlayerDeath: !this.runtimeTestingMode.disablePlayerDeath,
        enabled: true
      }, 'hotkey:F3');
    });
    this.bindKeyDown(this.earlyBossToggleKey, () => {
      this.applyRuntimeTestingModePatch({
        earlyBoss: !this.runtimeTestingMode.earlyBoss,
        enabled: true
      }, 'hotkey:F4');
    });
    this.bindKeyDown(this.pauseWavesToggleKey, () => {
      this.applyRuntimeTestingModePatch({
        pauseWaves: !this.runtimeTestingMode.pauseWaves,
        enabled: true
      }, 'hotkey:F5');
    });
    this.bindKeyDown(this.spawnEnemyHotkey, () => {
      this.spawnEnemyForDebug({
        enemyId: 'basic_enemy',
        source: 'hotkey:F6'
      });
    });
    this.bindKeyDown(this.giveWeaponHotkey, () => {
      this.giveWeaponForDebug({
        source: 'hotkey:F7'
      });
    });
    this.bindKeyDown(this.skipWaveHotkey, () => {
      this.skipWaveForDebug({
        source: 'hotkey:F8'
      });
    });

    this.logger.debug('setupTestingInputBindings:ready', {
      bindings: [
        'F1 diagnostics',
        'F2 noDamage',
        'F3 noDeath',
        'F4 earlyBoss',
        'F5 pauseWaves',
        'F6 spawnEnemy',
        'F7 giveWeapon',
        'F8 skipWave'
      ],
      testingEnabled: this.runtimeTestingMode.enabled
    });
  }

  private bindKeyDown(key: Phaser.Input.Keyboard.Key | undefined, handler: () => void): void {
    if (!key) return;
    key.on('down', handler);
    this.keyboardUnsubscribers.push(() => key.off('down', handler));
  }

  private bindEvent(type: string, listener: (event: any) => void): void {
    this.eventBus.on(type, listener);
    this.eventUnsubscribers.push(() => this.eventBus.off(type, listener));
  }

  private bindScaleEvent(type: string, listener: (...args: unknown[]) => void): void {
    if (typeof this.scale?.on !== 'function') return;
    this.scale.on(type, listener);
    this.scenePluginUnsubscribers.push(() => this.scale.off?.(type, listener));
  }

  private resizeSceneViewport(): void {
    const width = this.scale.width;
    const height = this.scale.height;
    this.logger.debug('resizeSceneViewport', { width, height });
    const mainCamera = this.cameras?.main;
    mainCamera?.setViewport?.(0, 0, width, height);
    mainCamera?.setSize?.(width, height);

    const zoom = this.getMainCameraZoom();
    const worldWidth = width / zoom;
    const worldHeight = height / zoom;

    this.background?.setSize?.(worldWidth, worldHeight);
    this.backgroundGrid?.setSize?.(worldWidth, worldHeight);
    this.controlsText?.setPosition?.(10 / zoom, worldHeight - 30 / zoom);
    this.hud?.resize?.();
    this.diagnosticsOverlay?.resize();
    this.pauseScreen?.resize?.();
    this.levelUpScreen?.resize?.();
    this.chestOpenScreen?.resize?.();
  }

  private refreshViewportAfterBrowserResize(source: string): void {
    this.clearViewportRefreshTimers();
    this.refreshScaleAndViewport(source, 'immediate');

    [100, 300].forEach(delayMs => {
      const timer = setTimeout(() => {
        this.refreshScaleAndViewport(source, `${delayMs}ms`);
      }, delayMs);
      this.viewportRefreshTimers.push(timer);
    });

    this.logger.debug('viewport-refresh:scheduled', { source });
  }

  private refreshScaleAndViewport(source: string, phase: string): void {
    this.scale?.refresh?.();
    this.resizeSceneViewport();
    this.mobileControlsOverlay?.resize();
    this.orientationOverlay?.resize();
    this.logger.debug('viewport-refresh:applied', {
      source,
      phase,
      width: this.scale.width,
      height: this.scale.height
    });
  }

  private clearViewportRefreshTimers(): void {
    this.viewportRefreshTimers.splice(0).forEach(timer => clearTimeout(timer));
  }

  private getMainCameraZoom(): number {
    const zoom = this.cameras?.main?.zoom;
    return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  }

  private applyRuntimeTestingModePatch(patch: Partial<RuntimeTestingModeState>, source: string): void {
    const nextState = updateRuntimeTestingModeState(this.runtimeTestingMode, patch, this.logger);
    this.runtimeTestingMode = nextState;
    this.effectiveGameSettings = applyRuntimeTestingOverrides(
      this.contentRegistry.getGameSettings(),
      nextState,
      this.logger
    );

    this.waveSpawnerSystem?.setRuntimeTestingMode(nextState);
    this.bossEncounterSystem?.setRuntimeTestingMode(nextState);
    this.collisionSystem?.setRuntimeTestingMode(nextState);
    this.combatFeedbackSystem?.setRuntimeTestingMode(nextState);
    if (!nextState.enabled) {
      this.lastDebugCommandResult = undefined;
    }
    this.setDiagnosticsOverlayVisibility(nextState.enabled && nextState.diagnosticsOverlay, source);

    this.logger.info('applyRuntimeTestingModePatch:applied', {
      source,
      nextState,
      effectiveGameSettings: this.effectiveGameSettings
    });
  }

  private setDiagnosticsOverlayVisibility(isVisible: boolean, source: string): void {
    if (!this.diagnosticsOverlay) return;
    this.diagnosticsOverlay.setPanelVisible(isVisible);
    this.logger.debug('setDiagnosticsOverlayVisibility', { isVisible, source });
  }

  private cleanupSceneRuntime(): void {
    this.clearViewportRefreshTimers();
    this.eventUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.keyboardUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.scenePluginUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.mobileInputController?.destroy();
    this.mobileControlsOverlay?.destroy();
    this.orientationOverlay?.destroy();
    this.hud?.dispose?.();
    this.experienceSystem?.dispose?.();
    this.lootSystem?.dispose?.();
    this.weaponSystem?.dispose?.();
    this.particleManager?.dispose?.();
    this.combatFeedbackSystem?.dispose?.();
    this.bossEncounterSystem?.dispose();
    this.waveSpawnerSystem?.dispose();
    this.logger.debug('cleanupSceneRuntime:complete');
  }

  public getRuntimeTestingModeState(): RuntimeTestingModeState {
    return this.runtimeTestingMode;
  }

  public getMobileControlsSnapshotForE2E(): MobileInputSnapshot | { active: false; reason: 'scene-not-ready' } {
    if (!this.sceneRuntimeReady || !this.mobileInputController) {
      return { active: false, reason: 'scene-not-ready' };
    }

    return this.mobileInputController.getSnapshot();
  }

  public getOrientationSnapshotForE2E(): OrientationSnapshot | { active: false; reason: 'scene-not-ready' } {
    if (!this.sceneRuntimeReady || !this.orientationOverlay || !this.mobileInputController) {
      return { active: false, reason: 'scene-not-ready' };
    }

    return this.orientationOverlay.getSnapshot(this.mobileInputController.getSnapshot().touchCapable);
  }

  public setRuntimeTestingModePatch(patch: Partial<RuntimeTestingModeState>, source: string = 'e2e'): RuntimeTestingModeState {
    this.applyRuntimeTestingModePatch(patch, source);
    return this.runtimeTestingMode;
  }

  public isDiagnosticsOverlayVisible(): boolean {
    return Boolean(this.diagnosticsOverlay?.visible);
  }

  public getDiagnosticsSnapshot(): {
    testingMode: RuntimeTestingModeState;
    overlayVisible: boolean;
    wave: ReturnType<WaveSpawnerSystem['getDiagnosticsSnapshot']>;
    bossEncounter: ReturnType<BossEncounterSystem['getDiagnosticsSnapshot']>;
    collision: ReturnType<CollisionSystem['getDiagnosticsSnapshot']>;
    pools: ReturnType<PoolManager['getPoolSnapshot']>;
    profile: {
      ok: boolean;
      schemaVersion?: number;
      totalRuns?: number;
      bestSurvivedSeconds?: number;
      lastRunResult?: string;
      unlockCount?: number;
      error?: string;
    };
    inventory: ReturnType<UpgradeSystem['getInventorySnapshot']>;
    playerStats: ReturnType<PlayerStats['getSnapshot']>;
    appVersion: ReturnType<typeof getAppVersionSnapshot>;
    visual: ReturnType<typeof getVisualDiagnosticsSummary>;
    combatVisuals: ReturnType<CombatFeedbackSystem['getDiagnosticsSnapshot']>;
    particles: ReturnType<ParticleManager['getDiagnosticsSnapshot']>;
    lastDebugCommandResult?: DebugCommandResult<unknown>;
  } {
    const profileSnapshot = this.getProfileSnapshotResult();
    return {
      combatVisuals: this.combatFeedbackSystem.getDiagnosticsSnapshot(),
      particles: this.particleManager.getDiagnosticsSnapshot(),
      testingMode: this.runtimeTestingMode,
      overlayVisible: this.isDiagnosticsOverlayVisible(),
      wave: this.waveSpawnerSystem.getDiagnosticsSnapshot(),
      bossEncounter: this.bossEncounterSystem.getDiagnosticsSnapshot(),
      collision: this.collisionSystem.getDiagnosticsSnapshot(),
      pools: this.poolManager.getPoolSnapshot(),
      profile: profileSnapshot.ok ? {
        ok: true,
        schemaVersion: profileSnapshot.profile.schemaVersion,
        totalRuns: profileSnapshot.profile.records.totalRuns,
        bestSurvivedSeconds: profileSnapshot.profile.records.bestSurvivedSeconds,
        lastRunResult: profileSnapshot.profile.records.lastRun?.result,
        unlockCount: profileSnapshot.profile.unlocks.weaponIds.length
      } : { ok: false, error: profileSnapshot.error },
      inventory: this.upgradeSystem.getInventorySnapshot(),
      playerStats: this.playerStats.getSnapshot(),
      appVersion: getAppVersionSnapshot(),
      visual: getVisualDiagnosticsSummary(this.visualFallbackSummary),
      lastDebugCommandResult: this.lastDebugCommandResult
    };
  }

  public getProfileSnapshotResult(): ReturnType<ProfileRuntime['getProfileSnapshot']> {
    this.logger.debug('getProfileSnapshotResult:start');
    return this.profileRuntime ? this.profileRuntime.getProfileSnapshot() : getSharedProfileRuntime().getProfileSnapshot();
  }

  private setCombatAnimationPaused(paused: boolean): void {
    if (paused && this.combatPauseStartedAt === null) this.combatPauseStartedAt = this.time.now;
    if (!paused && this.combatPauseStartedAt !== null) {
      const elapsed = Math.max(0, this.time.now - this.combatPauseStartedAt);
      const extendedStatuses = new Set<{ expiresAt: number }>();
      this.enemyGroup?.getChildren().forEach(child => {
        for (const key of ['burn', 'slow']) {
          const status = (child as Phaser.Physics.Arcade.Sprite).getData(key);
          if (status && !extendedStatuses.has(status)) {
            status.expiresAt += elapsed;
            extendedStatuses.add(status);
          }
        }
      });
      this.logger.debug('combat-animation:statuses-resumed', { elapsed, statuses: extendedStatuses.size });
      this.combatPauseStartedAt = null;
    }
    this.time.paused = paused;
    this.particleManager?.setPaused(paused);
    this.logger.debug('combat-animation:pause', { paused });
  }

  private setupEventListeners(): void {

    // Game Flow Events
    this.bindEvent('game:pause', (event: any) => {
      console.log('MainScene: Game Pause Event received', event);
      this.isPaused = true;
      this.mobileInputController?.setEnabled(false, `game:pause:${event?.data?.reason ?? 'unknown'}`);
      this.physics.pause();
      this.setCombatAnimationPaused(true);

      if (event.data && event.data.reason === 'levelup') {
        console.log('MainScene: Showing Level Up Screen');
        this.showLevelUpScreen();
      }
    });

    this.bindEvent('game:resume', () => {
      // Обработчик XP мог синхронно открыть следующий заработанный уровень.
      if (this.levelUpScreen.visible || this.chestOpenScreen.visible) return;
      console.log('MainScene: Game Resume Event received');
      this.isPaused = false;
      this.mobileInputController?.setEnabled(true, 'game:resume');
      this.physics.resume();
      this.setCombatAnimationPaused(false);
    });

    this.bindEvent('enemy:spawned', (event: any) => {
      const enemy = event.data.enemy;
      if (enemy && enemy.getSprite) {
        const sprite = enemy.getSprite();
        if (sprite) {
          this.enemyGroup.add(sprite);
        }
      } else if (enemy && enemy.active !== undefined) {
        // Fallback if enemy IS the sprite (legacy)
        this.enemyGroup.add(enemy);
      }
    });

    this.bindEvent('boss:spawned', (event: any) => {
      const bossEncounter = (event?.data?.bossEncounter ?? undefined) as BossEncounterEventMetadata | undefined;
      const width = this.scale.width;
      const height = this.scale.height;
      const warningTextKey = bossEncounter?.warningTextKey ?? 'warning.boss';
      const warningText = LocalizationManager.getInstance().getText(warningTextKey);
      this.logger.info('boss:spawned-warning', {
        encounterId: bossEncounter?.encounterId,
        warningTextKey
      });

      const warningLabel = this.add.text(width / 2, height / 3, warningText, {
        fontSize: '48px',
        color: '#ff0000',
        fontStyle: 'bold',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 6
      }).setOrigin(0.5).setScrollFactor(0).setDepth(2000);

      warningLabel.setWordWrapWidth(width - 40).setFontSize(Math.min(40, width / 22));
      this.time.delayedCall(2400, () => warningLabel.destroy());
    });

    this.bindEvent('enemy:created', (event: any) => {
      // Handle creation via factory direct calls if any
      const enemy = event.data.enemy;
      if (enemy && enemy.getSprite) {
        const sprite = enemy.getSprite();
        if (sprite) this.enemyGroup.add(sprite);
      }
    });

    this.bindEvent('upgrade:selected', (event: any) => {
      console.log('MainScene: Upgrade selected event received:', event);

      // Проверяем структуру события
      const choice = (event.data ? event.data.choice : event.choice) as UpgradeOption;

      if (choice) {
        console.log('MainScene: Applying upgrade via UpgradeSystem:', choice.id);
        if (this.upgradeSystem.applyUpgrade(choice)) {
          this.persistWeaponUnlockFromUpgrade(choice);
          this.refreshAfterUpgrade('level-up');
        }
      } else {
        console.error('MainScene: Invalid upgrade event data:', event);
      }

      this.eventBus.publish({ type: 'game:resume' });
    });

    this.bindEvent('player:heal', (event: any) => {
      const amount = Number(event?.data?.amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        console.error('MainScene: Invalid player:heal payload', event);
        return;
      }

      const maxHealth = this.playerStats.get('maxHealth');
      const currentHealth = Number(this.player.getData('health') ?? maxHealth);
      const nextHealth = Phaser.Math.Clamp(currentHealth + amount, 0, maxHealth);
      this.player.setData('health', nextHealth);
      this.hud?.updateHealth(nextHealth, maxHealth);
      console.log('MainScene: Applied heal consumable', { amount, currentHealth, nextHealth, maxHealth });
    });

    this.bindEvent('enemy:killed', (event: any) => {
      const { id, bossEncounter } = event.data as { id?: string; bossEncounter?: BossEncounterEventMetadata };
      const gameSettings = this.effectiveGameSettings;
      const isRequiredBossKill = bossEncounter?.requiredForVictory === true;
      const isLegacyBossKill = !bossEncounter && id === gameSettings.bossEnemyId;
      const requiredEncounterIds = new Set(gameSettings.bossVictoryRequirements?.requiredEncounterIds ?? []);

      if (!bossEncounter && isBossLookingPayload(event.data)) {
        this.logger.warn('enemy:killed-missing-boss-encounter-metadata', { id });
      }

      if (bossEncounter) {
        this.eventBus.publish({ type: 'boss:killed', data: { bossEncounter, x: event.data.x, y: event.data.y } });
      }

      if (gameSettings.killBossToWin && (isRequiredBossKill || isLegacyBossKill)) {
        if (bossEncounter) {
          if (!requiredEncounterIds.has(bossEncounter.encounterId)) {
            this.logger.warn('boss:required-kill-not-in-victory-requirements', {
              encounterId: bossEncounter.encounterId,
              requiredEncounterIds: [...requiredEncounterIds]
            });
            return;
          }

          if (this.killedRequiredBossEncounterIds.has(bossEncounter.encounterId)) {
            this.logger.warn('boss:duplicate-required-kill-ignored', {
              encounterId: bossEncounter.encounterId,
              killed: [...this.killedRequiredBossEncounterIds]
            });
            return;
          }

          this.killedRequiredBossEncounterIds.add(bossEncounter.encounterId);
          this.bossesKilled = this.killedRequiredBossEncounterIds.size;
        } else {
          this.bossesKilled++;
        }
        const requiredBosses = gameSettings.bossesToWin;
        this.logger.info('boss:progress', {
          killed: this.bossesKilled,
          required: requiredBosses,
          encounterId: bossEncounter?.encounterId,
          enemyId: id
        });
        
        if (isLegacyBossKill) {
          this.eventBus.publish({ type: 'boss:killed', data: { bossEncounter, x: event.data.x, y: event.data.y } });
        }

        if (this.bossesKilled >= requiredBosses) {
          if (this.victoryTriggered) {
            return;
          }
          this.victoryTriggered = true;
          this.logger.info('boss:victory-threshold-reached', {
            killed: this.bossesKilled,
            required: requiredBosses
          });
          if (this.runtimeTestingMode.enabled) {
            this.handleVictory();
            return;
          }

          // Delay slightly to show death animation.
          this.time.delayedCall(1000, () => {
            this.handleVictory();
          });
        }
      }
    });

    this.bindEvent('player:dead', (event: any) => {
      console.log('MainScene: Player Dead Event received', event);
      this.finalizeRunAndStartResultScene('defeat');
    });

    this.bindEvent('player:hit', (event: any) => {
      const currentHp = Number(event?.data?.currentHp);
      if (!Number.isFinite(currentHp)) return;
      this.hud?.updateHealth(currentHp, this.playerStats.get('maxHealth'));
    });

    this.bindEvent('chest:opened', () => {
      console.log('MainScene: Chest Opened');
      this.isPaused = true;
      this.mobileInputController?.setEnabled(false, 'chest:opened');
      this.setCombatAnimationPaused(true);
      this.physics.pause();

      const reward = this.upgradeSystem.getChestReward();
      if (reward.mode === 'evolution') {
        this.chestOpenScreen.showEvolution(reward.evolution);
      } else {
        this.chestOpenScreen.showUpgrades(reward.upgrades);
      }
      // audioManager.playSfx('chest_open')
    });

    this.bindEvent('chest:closed', (event: any) => {
      console.log('MainScene: Chest Closed');
      if (!this.levelUpScreen.visible) {
        this.isPaused = false;
        this.mobileInputController?.setEnabled(true, 'chest:closed');
        this.setCombatAnimationPaused(false);
        this.physics.resume();
      }

      if (event && event.data && event.data.evolution) {
          const { baseWeapon, evolvedWeapon } = event.data.evolution;
          const result = this.upgradeSystem.evolveWeapon(baseWeapon, evolvedWeapon);
          if (result.success) {
            this.refreshAfterUpgrade('evolution');
            this.combatFeedbackSystem.playRewardFeedback('evolutionReveal');
          } else {
            this.logger.warn('chest:closed evolution apply failed', { baseWeapon, evolvedWeapon, result });
          }
      } else if (Array.isArray(event?.data?.upgrades)) {
          const upgrades = event.data.upgrades as UpgradeOption[];
          let accepted = 0;
          upgrades.forEach(upgrade => {
              if (this.upgradeSystem.applyUpgrade(upgrade)) {
                accepted += 1;
                this.persistWeaponUnlockFromUpgrade(upgrade);
              }
          });

          this.logger.debug('chest:reward-applied', { accepted, rejected: upgrades.length - accepted });
          if (accepted > 0) {
            this.refreshAfterUpgrade('chest');
            this.combatFeedbackSystem.playRewardFeedback('chestReward');
          }
      }
    });
  }

  private refreshAfterUpgrade(source: string): void {
    console.log('MainScene: Refresh after upgrade START', { source });
    const maxHealth = this.playerStats.get('maxHealth');
    const currentHealth = Number(this.player.getData('health') ?? maxHealth);
    const clampedHealth = Phaser.Math.Clamp(currentHealth, 0, maxHealth);
    this.player.setData('health', clampedHealth);
    this.lootSystem?.refreshPickupRange();

    if (this.hud) {
      this.hud.updateStats(this.playerStats);
      this.hud.updateHealth(clampedHealth, maxHealth);
      this.hud.updateInventory(this.upgradeSystem.getInventorySnapshot());
    }

    console.log('MainScene: Refresh after upgrade END', {
      source,
      currentHealth: clampedHealth,
      maxHealth,
      inventory: this.upgradeSystem.getInventorySnapshot()
    });
  }

  private persistWeaponUnlockFromUpgrade(choice: UpgradeOption): void {
    if (choice.type !== 'weapon_unlock' || !choice.weaponId) return;
    const result = this.profileRuntime.unlockWeapon(choice.weaponId);
    if (!result.ok) {
      this.logger.error('persistWeaponUnlockFromUpgrade:failed', { weaponId: choice.weaponId, error: result.error });
    }
  }

  private handleVictory(): void {
    if (this.victorySceneStarted) {
      this.logger.debug('handleVictory:already-started');
      return;
    }

    this.victorySceneStarted = true;
    this.finalizeRunAndStartResultScene('victory');
  }

  private finalizeRunAndStartResultScene(result: RunResult): void {
    if (this.runFinalized) {
      this.logger.debug('finalizeRunAndStartResultScene:duplicate-ignored', { result });
      return;
    }

    this.runFinalized = true;
    this.isPaused = true;
    this.mobileInputController?.setEnabled(false, `result:${result}`);
    this.physics.pause();
    const summary = this.createRunSummary(result);
    const commit = this.profileRuntime.commitRun(summary);
    if (!commit.ok) {
      this.logger.error('finalizeRunAndStartResultScene:persist-failed', {
        result,
        error: commit.error,
        reason: commit.reason
      });
    } else {
      this.profileSnapshot = commit.profile;
      this.logger.info('finalizeRunAndStartResultScene:committed', {
        result,
        summary,
        totalRuns: commit.profile.records.totalRuns
      });
    }

    const sceneKey = result === 'victory' ? 'VictoryScene' : 'GameOverScene';
    this.scene.start(sceneKey, { score: summary.level, summary, profile: commit.profile });
  }

  private createRunSummary(result: RunResult): RunSummary {
    return {
      result,
      survivedSeconds: Math.max(0, this.gameplayElapsedSeconds),
      level: this.experienceSystem.getLevel(),
      bossesKilled: this.bossesKilled,
      endedAt: new Date().toISOString()
    };
  }

  private showLevelUpScreen(): void {
    console.log('MainScene: showLevelUpScreen called');

    // Get real upgrade choices
    const choices = this.upgradeSystem.getUpgradeChoices(3);

    // Map UpgradeOption to LevelUpScreen format if needed (they match mostly)
    // LevelUpScreen expects { id, title, description }
    // UpgradeOption has extra fields but is compatible

    this.levelUpScreen.showChoices(choices);
    // Ensure UI is on top
    this.levelUpScreen.setDepth(1000);
    this.levelUpScreen.setVisible(true); // Ensure visible

    console.log('MainScene: LevelUpScreen visibility set to true, depth:', this.levelUpScreen.depth);
  }

  private initializeGameSystems(): void {
    // Создаем игрока в центре экрана
    const { width, height } = this.scale;
    this.player = this.entityFactoryAdapter.createPlayer(width / 2, height / 2);
    this.player.setDepth(20);

    // Enable camera follow
    this.cameras.main.setZoom(CAMERA_INITIAL_ZOOM);
    this.logger.info('camera:zoom-initialized', {
      zoom: CAMERA_INITIAL_ZOOM
    });
    this.cameras.main.startFollow(this.player, false, CAMERA_FOLLOW_LERP_X, CAMERA_FOLLOW_LERP_Y);
    this.logger.info('camera:follow-initialized', {
      target: 'player',
      lerpX: CAMERA_FOLLOW_LERP_X,
      lerpY: CAMERA_FOLLOW_LERP_Y
    });

    // Initialize Player Health
    if (this.playerStats) {
      this.player.setData('health', this.playerStats.get('maxHealth'));
    }

    // Инициализируем контроллер игрока
    this.playerController = new PlayerController(this, this.player, this.playerStats);
    this.playerController.setMovementVectorProvider(this.mobileInputController ?? null);

    // Инициализируем систему оружия
    if (this.weaponSystem) {
      this.weaponSystem.init(this.player, this.playerStats);
      this.weaponSystem.setAimProvider(this.mobileInputController ?? null);
      this.profileWeaponIds.forEach(weaponId => {
        this.weaponSystem.unlockWeapon(weaponId);
      });
    }

    // Инициализируем систему лута
    if (this.lootSystem) {
      this.lootSystem.init(this.player, this.playerStats);
    }

    this.mapSystem = new MapSystem({
      eventBus: this.eventBus,
      biomes: this.contentRegistry.getBiomes(),
      positionProvider: {
        getPosition: () => ({ x: this.player.x, y: this.player.y })
      },
      backgroundRenderer: this.createBiomeBackgroundRenderer()
    });

    // Загружаем data-driven таблицу волн
    if (this.waveSpawnerSystem) {
      this.waveSpawnerSystem.loadWaves([...this.contentRegistry.getWaveTable()]);
    }
  }


  override update(_time: number, _delta: number) {
    const mobileSnapshot = this.mobileInputController?.getSnapshot();
    if (mobileSnapshot) {
      this.mobileControlsOverlay?.update(mobileSnapshot);
      const orientationSnapshot = this.orientationOverlay?.update(mobileSnapshot.touchCapable);
      this.mobileInputController?.setEnabled(!orientationSnapshot?.overlayVisible && !this.isPaused, 'orientation/update');
    }

    if (this.isPaused) return;

    const deltaSeconds = Number.isFinite(_delta) && _delta > 0 ? _delta / 1000 : 0;
    this.gameplayElapsedSeconds += deltaSeconds;
    if (this.player) {
      const existing = this.player.getData('characterVisual');
      const visual = existing instanceof CharacterVisual ? existing : new CharacterVisual(this.player);
      visual.update(deltaSeconds);
    }
    this.combatFeedbackSystem?.update(deltaSeconds, this.enemyGroup?.getChildren() as Phaser.Physics.Arcade.Sprite[] ?? [], this.player);

    // Обновление управления игроком
    if (this.playerController) {
      this.playerController.update();

      if (this.mapSystem) {
        this.mapSystem.update(_delta);
      }

      // Update background scrolling
      if (this.background) {
        this.background.tilePositionX = this.cameras.main.scrollX;
        this.background.tilePositionY = this.cameras.main.scrollY;
      }
      if (this.backgroundGrid) {
        this.backgroundGrid.tilePositionX = this.cameras.main.scrollX;
        this.backgroundGrid.tilePositionY = this.cameras.main.scrollY;
      }
    }

    // Автоматическая стрельба
    if (this.weaponSystem) {
      this.weaponSystem.fire();
      this.weaponSystem.update(_delta);
    }

    // Обновление спавнера волн
    if (this.bossEncounterSystem) {
      this.bossEncounterSystem.update(deltaSeconds);
    }

    // Обновление спавнера волн
    if (this.waveSpawnerSystem) {
      this.waveSpawnerSystem.update(deltaSeconds);
    }

    // Обновление системы лута
    if (this.lootSystem) {
      this.lootSystem.update(_delta);
    }

    // 404: Basic Enemy AI (existing code)
    // Update Enemy Entities (AI & Movement)
    if (this.enemyGroup && this.player) {
      this.enemyGroup.getChildren().forEach((child: any) => {
        if (!child.active) child.getData('characterVisual')?.reset?.();
        if (child.active) {
          const existing = child.getData('characterVisual');
          const visual = existing instanceof CharacterVisual ? existing : new CharacterVisual(child);
          visual.update(deltaSeconds);
          const entity = child.getData('entity');
          if (entity && typeof entity.update === 'function') {
            entity.update(_delta / 1000, this.player);
          } else {
            // Fallback for legacy enemies
            const speed = child.getData('speed') || 100;
            this.physics.moveToObject(child, this.player, speed);
          }
        }
      });
    }

    // Update HUD Timer
    if (this.hud) {
      this.hud.update(_time, _delta); // Ensure HUD animations update

      const elapsedSeconds = this.gameplayElapsedSeconds;
      this.hud.updateTimer(elapsedSeconds);
      this.hud.updateWave(Math.max(0, this.waveSpawnerSystem.getDiagnosticsSnapshot().wavesStarted - 1));

      // Victory Condition
      // If NOT killBossToWin, use timer. 
      // If killBossToWin, timer is just for show (or until boss spawns).
      const gameSettings = this.effectiveGameSettings;
      if (!gameSettings.killBossToWin && elapsedSeconds >= gameSettings.bossSpawnTime + 120) {
        // Fallback if no boss mode: win after some time?
        // Or just rely on previous 5 mins.
        // Let's keep 5 mins if killBossToWin is false.
        if (elapsedSeconds >= 300 && !this.victoryTriggered) {
          this.victoryTriggered = true;
          this.logger.info('victory:timer-threshold-reached', { elapsedSeconds });
          this.handleVictory();
        }
      }
    }

    if (this.diagnosticsOverlay?.visible && (_time - this.diagnosticsLastRefreshTime) >= 180) {
      this.diagnosticsLastRefreshTime = _time;
      const waveSnapshot = this.waveSpawnerSystem.getDiagnosticsSnapshot();
      const bossEncounterSnapshot = this.bossEncounterSystem.getDiagnosticsSnapshot();
      const profileSnapshot = this.getProfileSnapshotResult();
      const encounterById = new Map((this.effectiveGameSettings.bossEncounters ?? []).map(encounter => [encounter.id, encounter] as const));
      const activeEncounter = bossEncounterSnapshot.activeEncounterId
        ? encounterById.get(bossEncounterSnapshot.activeEncounterId)
        : undefined;
      const nextEncounter = bossEncounterSnapshot.nextEncounterId
        ? encounterById.get(bossEncounterSnapshot.nextEncounterId)
        : undefined;
      const inventorySnapshot = this.upgradeSystem.getInventorySnapshot();
      this.diagnosticsOverlay.updateSnapshot({
        fps: _delta > 0 ? 1000 / _delta : 0,
        playerHp: Number(this.player.getData('health') ?? 0),
        enemyCount: this.countActiveEnemies(),
        waveIndex: waveSnapshot.currentWaveIndex,
        waveTime: waveSnapshot.elapsedWaveTime,
        bossActive: waveSnapshot.bossActive,
        bossesSpawned: waveSnapshot.bossesSpawned,
        bossesKilled: bossEncounterSnapshot.bossesKilled,
        bossSpawnTime: waveSnapshot.effectiveBossSettings.bossSpawnTime,
        bossesToWin: waveSnapshot.effectiveBossSettings.bossesToWin,
        pauseWaves: waveSnapshot.pauseWaves,
        activeBossEncounter: activeEncounter ? {
          id: activeEncounter.id,
          name: activeEncounter.displayName ?? activeEncounter.enemyId
        } : undefined,
        nextBossEncounter: nextEncounter ? {
          id: nextEncounter.id,
          spawnTime: nextEncounter.spawnTime
        } : undefined,
        scalingSummary: waveSnapshot.scalingSummary,
        poolStats: this.poolManager.getPoolSnapshot(),
        testingMode: this.runtimeTestingMode,
        inventory: {
          activeWeaponCount: inventorySnapshot.weapons.length,
          maxWeapons: inventorySnapshot.maxWeapons,
          activePassiveCount: inventorySnapshot.passives.length,
          maxPassives: inventorySnapshot.maxPassives,
          weapons: inventorySnapshot.weapons.map(weapon => ({
            id: weapon.id,
            level: weapon.level,
            maxLevel: weapon.maxLevel
          })),
          passives: inventorySnapshot.passives.map(passive => ({
            id: passive.id,
            level: passive.level,
            maxLevel: passive.maxLevel
          }))
        },
        playerStatsSnapshot: this.playerStats.getSnapshot(),
        appVersion: getAppVersionSnapshot(),
        visual: getVisualDiagnosticsSummary(this.visualFallbackSummary),
        lastDebugCommandResult: this.lastDebugCommandResult,
        lastDebugWaveAction: waveSnapshot.lastDebugWaveAction,
        profile: profileSnapshot.ok ? {
          ok: true,
          schemaVersion: profileSnapshot.profile.schemaVersion,
          totalRuns: profileSnapshot.profile.records.totalRuns,
          bestSurvivedSeconds: profileSnapshot.profile.records.bestSurvivedSeconds,
          lastRunResult: profileSnapshot.profile.records.lastRun?.result,
          unlockCount: profileSnapshot.profile.unlocks.weaponIds.length
        } : {
          ok: false,
          error: profileSnapshot.error
        }
      });
    }
  }

  private createBiomeBackgroundRenderer(): BiomeBackgroundRenderer {
    return {
      applyBiomeColors: (backgroundColor, gridColor) => {
        this.background.setTint(parseHexColorToNumber(backgroundColor));
        this.backgroundGrid.setTint(parseHexColorToNumber(gridColor));
      }
    };
  }
}

function parseHexColorToNumber(color: string): number {
  return Number.parseInt(color.replace(/^#/, ''), 16);
}

function resolveScaleEvent(name: keyof typeof Phaser.Scale.Events, fallback: string): string {
  return Phaser.Scale?.Events?.[name] ?? fallback;
}
